import { Api } from "@/core/interfaces"

export namespace GuestBot {
  export type Status = "working" | "banned"
  export type PayloadType = "text" | "photo" | "document"
  export type ParseMode = "html" | "md" | null
  export type LogStatus =
    | "enqueued"
    | "published"
    | "expired"
    | "dropped"
    | "failed"

  export interface Button {
    text: string
    url?: string
    callbackData?: string
  }

  export interface ResponsePayload {
    type: PayloadType
    text?: string
    parseMode?: ParseMode
    media?: string
    title?: string
    mime?: "application/pdf" | "application/zip"
    buttons?: Button[][]
  }

  export interface ResponseBinding {
    id?: string
    triggerText: string | null
    templateId: string
  }

  export interface Bot {
    id: string
    botId: number
    username: string | null
    status: Status
    delaySeconds: number
    lastError: string | null
    /** Имя бота (setMyName), ≤ 64. */
    name?: string | null
    /** Описание бота в пустом чате (setMyDescription), ≤ 512. */
    description?: string | null
    /** «О боте» в профиле (setMyShortDescription), ≤ 120. */
    shortDescription?: string | null
    createdAt?: string
    updatedAt?: string
    responses?: ResponseBinding[]
  }

  export interface Template {
    id: string
    name: string
    payload: ResponsePayload
    createdAt?: string
    updatedAt?: string
  }

  export interface LogRow {
    id: string
    botRef: string | null
    botId: number
    templateId: string | null
    delaySeconds: number
    status: LogStatus
    triggeredAt: string
    publishedAt: string | null
    error: string | null
    senderId: number | null
    senderUsername: string | null
    senderName: string | null
    chatId: number | null
    chatType: string | null
    chatTitle: string | null
    triggerText: string | null
    messageText: string | null
    queryId: string | null
    messageId: number | null
    replyType: string
    /** `campaign:<campaignId>` when the reply matched an active campaign link of this bot, else null. */
    source: string | null
  }

  /** A single bot↔template binding, denormalized with nested bot + template. */
  export interface GuestBotResponse {
    id: string
    triggerText: string | null
    /** Может быть null, если бот удалён (осиротевшая привязка). */
    bot: Bot | null
    /** Может быть null, если шаблон удалён (осиротевшая привязка). */
    template: Template | null
  }

  // --- pagination ---
  /** Envelope returned by every list endpoint. */
  export interface Paginated<T> {
    items: T[]
    total: number
  }
  export interface PageQuery {
    limit?: number
    offset?: number
  }
  export type PageReq = Api.Request<{ queryParams?: PageQuery }>

  // --- requests / responses ---
  export type BotsListRes = Paginated<Bot>
  export type BotsListReq = PageReq
  export type GetBotReq = Api.Request<{ pathParams: { id: string } }>
  export type GetBotRes = Bot

  export interface CreateBotBody {
    token: string
    delaySeconds?: number
  }
  export type CreateBotReq = Api.Request<{ body: CreateBotBody }>
  export type CreateBotRes = Bot

  export interface PatchBotBody {
    status?: Status
    delaySeconds?: number
  }
  export type PatchBotReq = Api.Request<{
    pathParams: { id: string }
    body: PatchBotBody
  }>
  export type PatchBotRes = Bot

  export type DeleteBotReq = Api.Request<{ pathParams: { id: string } }>

  // --- profile (text) ---
  /**
   * Тело обновления текстового профиля. Все поля опциональны — применяются
   * только переданные. Пустая строка "" очищает значение в Telegram.
   */
  export interface UpdateProfileBody {
    /** ≤ 64 символа. */
    name?: string
    /** ≤ 512 символов. */
    description?: string
    /** ≤ 120 символов. */
    shortDescription?: string
  }
  export type UpdateProfileReq = Api.Request<{
    pathParams: { id: string }
    body: UpdateProfileBody
  }>
  export type UpdateProfileRes = Bot

  /**
   * Синхронизация данных бота из Telegram в БД (POST /:id/profile/refresh).
   * Подтягивает актуальные имя/описания и т.п. и возвращает обновлённого бота.
   */
  export type RefreshProfileReq = Api.Request<{ pathParams: { id: string } }>
  export type RefreshProfileRes = Bot

  // --- profile photo ---
  export type SetProfilePhotoReq = Api.Request<{
    pathParams: { id: string }
    body: FormData
  }>
  export interface SetProfilePhotoRes {
    ok: boolean
  }
  export type GetProfilePhotoReq = Api.Request<{ pathParams: { id: string } }>
  export interface GetProfilePhotoRes {
    url: string | null
  }

  export type TemplatesListRes = Paginated<Template>
  export type TemplatesListReq = PageReq
  export interface TemplateBody {
    name: string
    payload: ResponsePayload
  }
  export type CreateTemplateReq = Api.Request<{ body: TemplateBody }>
  export type UpdateTemplateReq = Api.Request<{
    pathParams: { id: string }
    body: TemplateBody
  }>
  export type DeleteTemplateReq = Api.Request<{ pathParams: { id: string } }>
  export type TemplateRes = Template

  export interface SetResponsesBody {
    bindings: ResponseBinding[]
  }
  export type SetResponsesReq = Api.Request<{
    pathParams: { id: string }
    body: SetResponsesBody
  }>
  export type DeleteResponseReq = Api.Request<{
    pathParams: { id: string; responseId: string }
  }>

  // --- bindings (global) ---
  export type BindingsListRes = Paginated<GuestBotResponse>
  export type BindingsListReq = PageReq
  export interface CreateBindingBody {
    botId: string
    templateId: string
    triggerText?: string | null
  }
  export type CreateBindingReq = Api.Request<{ body: CreateBindingBody }>
  export type CreateBindingRes = GuestBotResponse

  // --- join jobs + chat pool ---
  export type JoinJobStatus =
    | "draft"
    | "joining"
    | "awaiting_approval"
    | "completed"
    | "failed"
    | "cancelled"
  export type JoinLinkStatus =
    | "pending_join"
    | "joined"
    | "approval_pending"
    | "failed"
    | "cancelled"
  export interface JoinLinksSummary {
    total: number
    pendingJoin: number
    joined: number
    approvalPending: number
    failed: number
    cancelled: number
  }
  export interface JoinJob {
    id: string
    status: JoinJobStatus
    joinStageChainId: string | null
    role: string
    lastError: string | null
    linksSummary: JoinLinksSummary
    /** Independent consent echoed on creation; legacy/list responses may omit it. */
    downloadMessages?: boolean
    analyzeAfterJoin?: boolean
    createdAt: string
    updatedAt: string
    /** Detail only (sorted by sortOrder); list items omit it. Wire name is items[] (delta §2.2, F2). */
    items?: JoinLink[]
  }
  export interface JoinLink {
    id: string
    /** As entered by the operator (not normalized). */
    url: string
    sortOrder: number
    status: JoinLinkStatus
    chatId: number | null
    jitterMs: number
    reason: string | null
    joinedAt: string | null
    createdAt: string
    updatedAt: string
  }
  /** delta §3.1: joinJobId always present (F5); joinedAt = first join; unique per (chatId, role).
   *  fraudScore/analyzedAt (§3) — последний анализ ЛЮБОГО источника; оба null —
   *  чат ещё ни разу не анализировался. Индикатор, не вердикт. */
  export interface PoolChat {
    chatId: number
    title: string | null
    type: string | null
    joinedAt: string
    role: string
    joinJobId: string
    fraudScore: number | null
    analyzedAt: string | null
  }

  // --- mailings ---
  export type MailingStatus =
    | "draft"
    | "sending"
    | "completed"
    | "failed"
    | "cancelled"
  export type MailingChatStatus =
    | "pending"
    | "sent"
    | "deleted"
    | "failed"
    | "cancelled"
  /** pending is optional per delta §4.3 (F3) — readers keep ?? 0. */
  export interface MailingChatsSummary {
    total: number
    pending?: number
    sent: number
    deleted: number
    failed: number
    cancelled: number
  }
  export interface Mailing {
    id: string
    botId: string
    botUsername: string | null
    /** Required key, nullable value (delta §4.3, F4). */
    botUsernameSnapshot: string | null
    templateId: string
    templateName: string
    status: MailingStatus
    mailingStageChainId: string | null
    /** Rev 3: `text` = resolved trigger base of the binding (delta §4.4 as
     *  amended) — used only by the MailingProgress read-only line
     *  «Опубликовано: <text> @username» (§5.5); never posted from the form. */
    text?: string
    role?: string
    pauseMsBetweenChats?: number
    jitterMaxMs?: number
    chatsSummary: MailingChatsSummary
    lastError: string | null
    createdAt: string
    updatedAt: string
    /** Wire name is items[] (delta §4.4, F2). */
    items?: MailingChat[]
  }
  export interface MailingChat {
    id: string
    chatId: number
    chatTitle: string | null
    chatType: string | null
    status: MailingChatStatus
    reason: string | null
    sentAt: string | null
    deletedAt: string | null
    createdAt: string
    updatedAt: string
  }

  // --- bodies ---
  /** role default "guest-bot"; tempo defaults 30000/3000.
   *  downloadMessages / analyzeAfterJoin default independently to false.
   *  Analysis requires explicit history download consent. */
  export interface JoinChatsBody {
    links: string[]
    role?: string
    pauseMsBetweenLinks?: number
    jitterMaxMs?: number
    downloadMessages?: boolean
    analyzeAfterJoin?: boolean
  }
  /** Rev 3: NO text/role (delta §4.1 as amended) — the posted message =
   *  binding trigger + @username, resolved server-side. */
  export interface MailingBody {
    templateId: string
    chatIds: number[]
    pauseMsBetweenChats?: number
    jitterMaxMs?: number
  }

  // --- req/res (10 endpoints, spec §3); create returns the full detail (201) ---
  export type CreateJoinJobReq = Api.Request<{ body: JoinChatsBody }>
  export type CreateJoinJobRes = JoinJob
  export type JoinJobsListReq = Api.Request<{ queryParams?: PageQuery }>
  export type JoinJobsListRes = Paginated<JoinJob>
  export type GetJoinJobReq = Api.Request<{ pathParams: { jobId: string } }>
  export type GetJoinJobRes = JoinJob
  export type CancelJoinJobReq = Api.Request<{ pathParams: { jobId: string } }>
  export type CancelJoinJobRes = JoinJob
  export type ChatsListReq = Api.Request<{
    queryParams?: { role?: string; search?: string } & PageQuery
  }>
  export type ChatsListRes = Paginated<PoolChat>
  export type BotChatsReq = Api.Request<{
    pathParams: { id: string }
    queryParams?: Omit<NonNullable<ChatsListReq["queryParams"]>, "role">
  }>
  export type BotChatsRes = Paginated<PoolChat>
  export type CreateMailingReq = Api.Request<{
    pathParams: { id: string }
    body: MailingBody
  }>
  export type CreateMailingRes = Mailing
  export type MailingsListReq = Api.Request<{ queryParams?: PageQuery }>
  export type MailingsListRes = Paginated<Mailing>
  export type GetMailingReq = Api.Request<{
    pathParams: { id: string; mailingId: string }
  }>
  export type GetMailingRes = Mailing
  export type CancelMailingReq = Api.Request<{
    pathParams: { id: string; mailingId: string }
  }>
  export type CancelMailingRes = Mailing

  // --- conflict guards (409 + non-empty id field; v1 isCampaignConflict pattern) ---
  export interface JoinJobConflictBody {
    message: string
    activeJoinJobId: string
  }
  export interface MailingConflictBody {
    message: string
    activeMailingId: string
  }
  export function isActiveJoinJobConflict(
    error: unknown,
  ): error is { status: 409; data: JoinJobConflictBody } {
    const e = error as { status?: unknown; data?: unknown } | null | undefined
    return (
      !(e == null || typeof e !== "object") &&
      e.status === 409 &&
      typeof e.data === "object" &&
      e.data !== null &&
      typeof (e.data as JoinJobConflictBody).activeJoinJobId === "string" &&
      (e.data as JoinJobConflictBody).activeJoinJobId !== ""
    )
  }
  /** The username-unknown 409 is a standard body WITHOUT activeMailingId → returns false (delta §8). */
  export function isActiveMailingConflict(
    error: unknown,
  ): error is { status: 409; data: MailingConflictBody } {
    const e = error as { status?: unknown; data?: unknown } | null | undefined
    return (
      !(e == null || typeof e !== "object") &&
      e.status === 409 &&
      typeof e.data === "object" &&
      e.data !== null &&
      typeof (e.data as MailingConflictBody).activeMailingId === "string" &&
      (e.data as MailingConflictBody).activeMailingId !== ""
    )
  }

  export interface LogsQuery {
    botId?: string
    status?: LogStatus
    limit?: number
    offset?: number
    /** Journal source filter, e.g. `mailing:<mailingId>` (legacy `campaign:<campaignId>`). */
    source?: string
  }
  export type LogsListReq = Api.Request<{ queryParams?: LogsQuery }>
  export type BotLogsListReq = Api.Request<{
    pathParams: { id: string }
    queryParams?: Omit<LogsQuery, "botId">
  }>
  export type LogsListRes = Paginated<LogRow>

  /* ----------------------- Provision (создание через BotFather) ----------------------- */

  export type ProvisionStatus =
    | "draft"
    | "provisioning"
    | "completed"
    | "failed"

  /** Запись создания бота. Токен сервер никогда не возвращает — не ждём его. */
  export interface ProvisionJob {
    id: string
    role: string
    title: string
    username: string | null
    usernameDictionary: string[]
    about: string | null
    description: string | null
    settings: Record<string, 0 | 1 | string>
    status: ProvisionStatus
    botId: number | null
    /** Финальный юзернейм без @. */
    usernameSnapshot: string | null
    /** true — переиспользован существующий бот аккаунта-исполнителя. */
    adopted: boolean
    delaySeconds: number
    lastError: string | null
    stageChainIds: string[]
    createdAt: string
    updatedAt: string
  }

  export interface CreateProvisionBody {
    title: string
    username: string
    about?: string
    description?: string
    settings?: Record<string, 0 | 1 | string>
    role?: string
    delaySeconds?: number
  }
  export type CreateProvisionReq = Api.Request<{ body: CreateProvisionBody }>
  export type ProvisionsListReq = Api.Request<{
    queryParams?: { limit?: number; offset?: number }
  }>
  export type ProvisionsListRes = Paginated<ProvisionJob>
}
